import { useEffect, useRef, useState } from "react";
import {
  enregistrerTauxFraisMiseEnPlace,
  watchTauxFraisMiseEnPlace,
  enregistrerFournisseurs,
  demarrerRepartitionLP,
  normFournisseur,
} from "../oilmed.service";
import { useFournisseurs, useTerminalStocks } from "../useOilmed";
import { fmt, showToast } from "../utils";

const TERMINAUX = ["SEP", "LP"];

// "10 000", "10000 CDF", "10.000" → 10000 ; champ vide → null (taux inchangé)
function parseTaux(value) {
  const raw = String(value ?? "")
    .replace(/[^\d,]/g, "")
    .replace(",", ".");
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Onglet Paramètres (admins uniquement) : taux des frais mise en place en
 * CDF/m³ pour chaque terminal (pré-remplissent le montant déclaré d'un
 * programme), fournisseurs du libre pratique et soldes d'ouverture de la
 * répartition du LP par fournisseur.
 */
export default function GParametres() {
  const [taux, setTaux] = useState({ SEP: "", LP: "" });
  const [charge, setCharge] = useState(false);
  const [saving, setSaving] = useState(false);
  const dejaCharge = useRef(false);

  useEffect(
    () =>
      watchTauxFraisMiseEnPlace((t) => {
        // N'écrase pas une saisie en cours : seulement au premier chargement
        if (dejaCharge.current) return;
        dejaCharge.current = true;
        setTaux({
          SEP: t.SEP != null ? String(t.SEP) : "",
          LP: t.LP != null ? String(t.LP) : "",
        });
        setCharge(true);
      }),
    [],
  );

  const valeurs = Object.fromEntries(
    TERMINAUX.map((t) => [t, parseTaux(taux[t])]),
  );
  // Au moins un taux saisi, et chaque taux saisi doit être un nombre ≥ 0
  const valide =
    TERMINAUX.some((t) => valeurs[t] !== null) &&
    TERMINAUX.every((t) => valeurs[t] === null || valeurs[t] >= 0);

  async function enregistrer() {
    if (!valide) return;
    setSaving(true);
    try {
      await enregistrerTauxFraisMiseEnPlace(valeurs);
      showToast("Paramètres enregistrés ✔");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-title">
          Paramètres <span>Gestock</span>
        </div>
        <div className="page-sub">{"// "}RÉSERVÉ AUX ADMINISTRATEURS</div>
      </div>

      <Bloc titre="💰 Frais de mise en place" badge="CDF / m³">
        {TERMINAUX.map((t) => (
          <div key={t} className="form-group">
            <label>Frais de mise en place {t} (CDF par m³)</label>
            <input
              type="text"
              inputMode="decimal"
              value={taux[t]}
              placeholder={charge ? "Ex: 10000" : "Chargement…"}
              onChange={(e) =>
                setTaux((prev) => ({ ...prev, [t]: e.target.value }))
              }
            />
          </div>
        ))}
        <button
          className="btn btn-green btn-full"
          disabled={!valide || saving}
          onClick={enregistrer}
        >
          {saving ? "En cours…" : "✔ Enregistrer"}
        </button>
      </Bloc>

      <FournisseursCard />

      {TERMINAUX.map((t) => (
        <RepartitionCard key={t} terminal={t} />
      ))}
    </div>
  );
}

/** Bloc repliable, fermé par défaut (même en-tête que les boîtes du LP). */
function Bloc({ titre, badge, children, style }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={style}>
      <div
        className="card-header"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)
        }
      >
        <span className="card-title">
          <span
            style={{
              display: "inline-block",
              width: 14,
              color: "var(--muted)",
            }}
          >
            {open ? "▾" : "▸"}
          </span>
          {titre}
        </span>
        {badge && <span className="badge badge-blue">{badge}</span>}
      </div>
      {open && <div style={{ padding: 14 }}>{children}</div>}
    </div>
  );
}

// "120,679" / "120.679" / "1 200,5" → nombre ; vide → null
function parseM3(value) {
  const raw = String(value ?? "")
    .trim()
    .replace(/[\s\u202F\u00A0]/g, "");
  if (!raw) return null;
  const norm = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  const n = Number(norm);
  return Number.isFinite(n) ? n : NaN;
}

const PRODUITS = [
  { id: "essence", label: "Super" },
  { id: "go", label: "Go" },
];

/** Liste des fournisseurs du libre pratique, par terminal. */
function FournisseursCard() {
  const { parTerminal } = useFournisseurs();
  const [nouveau, setNouveau] = useState({ SEP: "", LP: "" });
  const [saving, setSaving] = useState(false);

  async function enregistrer(terminal, liste) {
    setSaving(true);
    try {
      await enregistrerFournisseurs(terminal, liste);
      showToast("Fournisseurs enregistrés ✔");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function ajouter(terminal) {
    const nom = normFournisseur(nouveau[terminal]);
    if (!nom) return;
    const liste = parTerminal[terminal] || [];
    if (liste.includes(nom)) return showToast(`${nom} existe déjà`, "error");
    await enregistrer(terminal, [...liste, nom]);
    setNouveau((prev) => ({ ...prev, [terminal]: "" }));
  }

  return (
    <Bloc
      titre="🏷 Fournisseurs libre pratique"
      badge="PAR TERMINAL"
      style={{ marginTop: 14 }}
    >
      {TERMINAUX.map((t) => {
        const liste = parTerminal[t] || [];
        return (
          <div key={t} className="form-group">
            <label>Fournisseurs {t}</label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 8,
              }}
            >
              {liste.length === 0 && (
                <span style={{ fontSize: 12, color: "var(--muted)" }}>
                  Aucun fournisseur
                </span>
              )}
              {liste.map((f) => (
                <span
                  key={f}
                  className="badge badge-blue"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {f}
                  <button
                    title={`Retirer ${f}`}
                    disabled={saving}
                    onClick={() =>
                      enregistrer(
                        t,
                        liste.filter((x) => x !== f),
                      )
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 13,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={nouveau[t]}
                placeholder="Ex: KEMEXON"
                onChange={(e) =>
                  setNouveau((prev) => ({ ...prev, [t]: e.target.value }))
                }
                onKeyDown={(e) => e.key === "Enter" && ajouter(t)}
              />
              <button
                className="btn btn-green"
                disabled={!normFournisseur(nouveau[t]) || saving}
                onClick={() => ajouter(t)}
              >
                ＋ Ajouter
              </button>
            </div>
          </div>
        );
      })}
    </Bloc>
  );
}

/** Répartition du LP par fournisseur d'un terminal : état + soldes d'ouverture. */
function RepartitionCard({ terminal }) {
  const { stocks, loading } = useTerminalStocks(terminal);
  const { liste } = useFournisseurs(terminal);

  return (
    <Bloc
      titre={`⚖️ Répartition libre pratique ${terminal}`}
      badge="PAR FOURNISSEUR"
      style={{ marginTop: 14 }}
    >
      {PRODUITS.map((p) => (
        <RepartitionProduit
          key={p.id}
          terminal={terminal}
          produit={p}
          stock={loading ? null : stocks?.[p.id]}
          fournisseurs={liste}
        />
      ))}
    </Bloc>
  );
}

function RepartitionProduit({ terminal, produit, stock, fournisseurs }) {
  const [edition, setEdition] = useState(false);
  const [soldes, setSoldes] = useState({});
  const [saving, setSaving] = useState(false);
  const rep = stock?.repartition?.demarree ? stock.repartition : null;
  const total = stock?.lp ?? null;

  function ouvrir() {
    // Un seul fournisseur : tout le LP lui revient par défaut
    setSoldes(
      fournisseurs.length === 1 && total !== null
        ? {
            [fournisseurs[0]]: String(Math.round(total * 1000) / 1000).replace(
              ".",
              ",",
            ),
          }
        : {},
    );
    setEdition(true);
  }

  const valeurs = Object.fromEntries(
    fournisseurs.map((f) => [f, parseM3(soldes[f])]),
  );
  const somme = Object.values(valeurs).reduce(
    (a, v) => a + (Number.isFinite(v) ? v : 0),
    0,
  );
  const invalide = Object.values(valeurs).some((v) => Number.isNaN(v));
  const ecart = total === null ? null : total - somme;
  const valide = !invalide && ecart !== null && Math.abs(ecart) <= 0.001;

  async function demarrer() {
    if (!valide) return;
    setSaving(true);
    try {
      const ouverture = Object.fromEntries(
        Object.entries(valeurs).filter(([, v]) => v !== null),
      );
      await demarrerRepartitionLP(terminal, produit.id, ouverture);
      showToast(`Répartition ${terminal} ${produit.label} démarrée ✔`);
      setEdition(false);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 6,
        }}
      >
        <strong>{produit.label}</strong>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
          LP total : {total === null ? "…" : `${fmt(total)} m³`}
        </span>
      </div>

      {rep ? (
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          <div style={{ color: "var(--muted)", marginBottom: 4 }}>
            Suivi depuis le{" "}
            {rep.depuis?.toDate?.()?.toLocaleDateString("fr-FR") ?? "…"}
          </div>
          {Object.entries(rep.parFournisseur).map(([f, v]) => (
            <div
              key={f}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>{f}</span>
              <span style={{ fontFamily: "var(--font-mono)" }}>
                {fmt(v)} m³
              </span>
            </div>
          ))}
          {Math.abs(rep.nonAttribue) > 0.0005 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "var(--gold)",
              }}
            >
              <span>Non attribué</span>
              <span style={{ fontFamily: "var(--font-mono)" }}>
                {fmt(rep.nonAttribue)} m³
              </span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
          Répartition par fournisseur non démarrée.
        </div>
      )}

      {!edition ? (
        <button
          className="btn btn-ghost btn-full"
          disabled={fournisseurs.length === 0 || total === null}
          title={
            fournisseurs.length === 0
              ? `Ajoutez d'abord les fournisseurs ${terminal}`
              : ""
          }
          onClick={ouvrir}
        >
          {rep ? "Redéfinir les soldes d'ouverture" : "Démarrer la répartition"}
        </button>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
            Saisissez le solde actuel de chaque fournisseur. Le total doit être
            égal au libre pratique actuel ({fmt(total)} m³).
            {rep && " Les soldes actuels seront remplacés."}
          </div>
          {fournisseurs.map((f) => (
            <div key={f} className="form-group">
              <label>{f} (m³)</label>
              <input
                type="text"
                inputMode="decimal"
                value={soldes[f] ?? ""}
                placeholder="0"
                onChange={(e) =>
                  setSoldes((prev) => ({ ...prev, [f]: e.target.value }))
                }
              />
            </div>
          ))}
          <div className="info-strip">
            <span>
              Total saisi :{" "}
              <span className={valide ? "" : "err"}>{fmt(somme)} m³</span>
            </span>
            {!valide && ecart !== null && !invalide && (
              <span style={{ color: "var(--red)" }}>
                Reste à répartir : {fmt(ecart)} m³
              </span>
            )}
          </div>
          <div className="btn-row">
            <button
              className="btn btn-green"
              style={{ flex: 1 }}
              disabled={!valide || saving}
              onClick={demarrer}
            >
              {saving ? "En cours…" : "✔ Enregistrer les soldes"}
            </button>
            <button className="btn btn-ghost" onClick={() => setEdition(false)}>
              Annuler
            </button>
          </div>
        </>
      )}
    </div>
  );
}
